// Shared low-poly mineral parts — a deterministic jitter stream (never
// Math.random, so a variant always rebuilds byte-identical geometry) and a
// scree-scatter primitive for the rubble rocks and crystals strew around their
// feet. Ported from age-of-mcp's v2/mineral-parts.ts: the pure geometry is kept
// verbatim, with the Part type taken from the library.

import * as THREE from "three";
import type { Part } from "../types";

/** Deterministic 0..1 hash stream keyed by (seed, n). */
export function h01(seed: number, n: number): number {
  const x = Math.sin(seed * 117.32 + n * 271.7 + 31.4) * 43758.5453;
  return x - Math.floor(x);
}

/** Linear blend between `a` and `b`. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * A deterministic ring-scatter of small faceted stones around the origin —
 * rubble at a boulder's foot, the strewn clump of `rocks`, scree under a
 * crystal cluster. Alternates rounded icosahedron pebbles with sharper
 * tetrahedron shards; every position, size and tilt comes from the
 * seed-keyed stream.
 */
export function scree(
  seed: number, count: number,
  dist: readonly [number, number], size: readonly [number, number],
  colors: readonly string[],
): Part[] {
  const parts: Part[] = [];
  for (let i = 0; i < count; i++) {
    const ang = (i / count) * Math.PI * 2 + h01(seed, i * 4) * 1.6;
    const d = lerp(dist[0], dist[1], h01(seed, i * 4 + 1));
    const r = lerp(size[0], size[1], h01(seed, i * 4 + 2));
    const shard = i % 2 === 1;
    parts.push({
      geometry: shard
        ? new THREE.TetrahedronGeometry(r * 1.3, 0)
        : new THREE.IcosahedronGeometry(r, 0),
      color: colors[i % colors.length]!,
      position: [Math.cos(ang) * d, r * 0.55, Math.sin(ang) * d],
      rotation: [
        h01(seed, i * 4 + 3) * 0.9,
        h01(seed, i * 7 + 11) * Math.PI * 2,
        h01(seed, i * 5 + 23) * 0.9,
      ],
      scale: [1.15, 0.78, 1.0],
    });
  }
  return parts;
}
